import { useState, useCallback } from 'react'
import {
  dateFormats,
  SLOTS_ROW_LIMIT_V3,
  SAD_PATH_SLOTS_ROW_LIMIT,
  TimeOfDay,
} from 'constants/common'
import formatFNS from 'date-fns/format'
import Row from 'react-bootstrap/Row'
import styles from './index.module.scss'
import VerticalSlotGrid from './VerticalSlotGrid'
import {
  SlotAvailabilityType,
  slotAvailabilityEnumMapping,
} from 'app/Bookings/constants'
import useThriveAuth from 'hooks/useThriveAuth'
import Button from 'react-bootstrap/Button'
import classNames from 'classnames'
import SlotGridButton from 'common/SlotGridButton'

type SlotGridProps = {
  providerDetails: Provider
  dates: Date[]
  generatedSlots: DateSlot
  className?: string
  showMoreButton?: boolean
  showLimitedSlots?: boolean
  showSlotSelection?: boolean
  showSlotAvailabilityIcon?: boolean
  slotGridButtonClass?: string
  emptySlotClass?: string
  selectedSlot?: SelectedSlot
  onSlotClick?: (selectedSlot: SelectedSlot) => void
  appointmentMode?: SlotAvailabilityType
  isProviderPage?: boolean
}

const SlotGrid: React.FC<SlotGridProps> = ({
  providerDetails,
  dates,
  generatedSlots,
  className = '',
  showMoreButton = false,
  showLimitedSlots = false,
  showSlotAvailabilityIcon = false,
  selectedSlot = null,
  slotGridButtonClass = '',
  emptySlotClass = '',
  onSlotClick,
  showSlotSelection = true,
  appointmentMode,
  isProviderPage = false,
}) => {
  //If we want show more functionality, then initialy, showLimitedSlots else show all slots
  const [showMore, setShowMore] = useState<boolean>(
    !showMoreButton && !showLimitedSlots
  )
  const [hideGradient, setHideGradient] = useState<boolean>(false)
  const { isSupportOrThirdPartyUser, isAuthenticated } = useThriveAuth()
  const { id } = providerDetails

  const onCardClick = (slot: ProviderSlot) => {
    !!onSlotClick &&
      onSlotClick({
        slot,
        provider: providerDetails,
      })
  }

  const getCurrentDaySlots = (date: Date) =>
    generatedSlots[formatFNS(date, dateFormats.YEAR_MONTH_DAY_FORMAT)]

  const isSlotValid = (slot: ProviderSlot) =>
    slot?.start && slot?.practiceId === providerDetails.practice?.id

  const getValidSlots = (slots: ProviderSlot[]) =>
    slots?.filter((slot) => isSlotValid(slot))

  const getMaxNumberOfSlots = (dates) => {
    let maxNoOfSlots = getCurrentDaySlots(dates[0]).length

    for (let i = 1; i < dates.length; i++) {
      if (getCurrentDaySlots(dates[i])?.length > maxNoOfSlots) {
        maxNoOfSlots = getCurrentDaySlots(dates[i]).length
      }
    }

    return maxNoOfSlots
  }

  const maxSlots = isProviderPage && getMaxNumberOfSlots(dates)

  const SLOT_ROW_LIMIT = maxSlots || SLOTS_ROW_LIMIT_V3

  const handleScroll = useCallback(({ target }) => {
    // Check if the user has scrolled to the bottom
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 1) {
      setHideGradient(true)
    } else {
      setHideGradient(false)
    }
  }, [])

  return (
    <div className={`${className} flex-fill`}>
      <Row
        noGutters
        className={classNames('d-none d-lg-flex flex-nowrap', className, {
          [styles.slots_container]:
            isProviderPage && maxSlots > SAD_PATH_SLOTS_ROW_LIMIT,
          [styles.slots_container_authenticated]:
            isProviderPage &&
            isAuthenticated &&
            maxSlots > SAD_PATH_SLOTS_ROW_LIMIT,
          [styles.slots_container_support]:
            isProviderPage &&
            isAuthenticated &&
            isSupportOrThirdPartyUser &&
            maxSlots > SAD_PATH_SLOTS_ROW_LIMIT,
          [styles.hide_gradient]: hideGradient,
        })}
        data-testid={`${providerDetails.id}-slots`}
        onScroll={isProviderPage ? handleScroll : undefined}
      >
        {dates.map((date) => {
          const currentDaySlots = getCurrentDaySlots(date)

          return (
            <div
              key={`${id}-${date.getDate()}-${date.getMonth()}`}
              className={styles.day_slots_layout}
            >
              {currentDaySlots?.map((slot, index) => {
                if (index < SLOT_ROW_LIMIT || showMore) {
                  if (isSlotValid(slot)) {
                    const isSlotSelected = selectedSlot?.slot?.id === slot?.id
                    const slotAvailabilities = slot?.availabilityType?.map(
                      (availability) =>
                        slotAvailabilityEnumMapping[availability]
                    )
                    const isEveningSlot = slot?.timeOfDay === TimeOfDay.EVENING
                    const isDaySlot = slot?.timeOfDay && !isEveningSlot

                    return (
                      <SlotGridButton
                        buttonVariant="outline-info"
                        key={`card-${id}-${index}`}
                        slotButtonClass={classNames(
                          {
                            [styles.slot_button]: !isProviderPage,
                            [styles.slot_button_provider_page]: isProviderPage,
                          },
                          slotGridButtonClass,
                          {
                            [styles.slot_button_acted]:
                              isSlotSelected && showSlotSelection,
                            [styles.slot_button_support_override]: isSupportOrThirdPartyUser,
                            [styles.slot_button_daySlot]: isDaySlot,
                            [styles.slot_button_eveningSlot]: isEveningSlot,
                          }
                        )}
                        slot={slot}
                        onSlotClick={() => onCardClick(slot)}
                        showSlotAvailabilityIcon={showSlotAvailabilityIcon}
                        slotAvailabilities={
                          appointmentMode !== undefined
                            ? [appointmentMode]
                            : slotAvailabilities
                        }
                        isProviderPage
                      />
                    )
                  }

                  return (
                    <div
                      key={`card-${id}-${index}`}
                      className={classNames(
                        {
                          [styles.empty_slot_placeholder]: !isProviderPage,
                          [styles.empty_slot_placeholder_provider_page]: isProviderPage,
                        },
                        emptySlotClass,
                        'px-1',
                        {
                          [styles.empty_slot_placeholder_support_override]: isSupportOrThirdPartyUser,
                        }
                      )}
                    >
                      —
                    </div>
                  )
                }
              })}

              {showMoreButton &&
                !showMore &&
                getValidSlots(currentDaySlots)?.length > SLOTS_ROW_LIMIT_V3 && (
                  <Button
                    variant="link"
                    className={styles.more_btn}
                    onClick={() => setShowMore(true)}
                  >
                    more
                  </Button>
                )}
            </div>
          )
        })}
      </Row>
      {/* Mobile responsive vertical view */}
      <div className="d-lg-none h-100">
        <VerticalSlotGrid
          providerDetails={providerDetails}
          dates={dates}
          generatedSlots={generatedSlots}
          showSlotSelection={showSlotSelection}
          selectedSlot={selectedSlot}
          onCardClick={onCardClick}
        />
      </div>
    </div>
  )
}

export default SlotGrid
